import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type CSSProperties,
} from 'react';
import { useMedia } from 'react-use';
import { convertPairingListResultV3ToPairingV1WithIngredients } from '@hungryroot/cart';
import {
  useCustomerDietaryRestrictions,
  useDietaryRestrictions as useAllDietaryRestrictions,
} from '@hungryroot/dietary-restrictions';
import {
  getFullStorySessionUrl,
  useIsEmbeddedWebView,
} from '@hungryroot/environment';
import { useFeatureFlag } from '@hungryroot/feature-flags';
import { PDPModal } from '@hungryroot/rdp';
import {
  type AgentSurface,
  type PairingListResultV3,
  type Product,
  type SuggestedPromptVariant,
} from '@hungryroot/rest';
import { useSmartCartAgentChats, useTagGroup } from '@hungryroot/rest-hooks';
import {
  buttonClickedEvent,
  componentInteraction,
  pairingInteraction,
  productInteraction,
} from '@hungryroot/segment';
import {
  tagGroups,
  tags as TagEnum,
  useCustomerTagGroup,
  useCustomerTags,
} from '@hungryroot/tags';
import { BreakpointQueryLarge, CaretDown, CaretUp } from '@hungryroot/ui-kit';
import { usePdpFooter } from '../../components/PDP/usePdpFooter';
import { RDPModal } from '../../components/RDP/RDPModal/RDPModal';
import { RecipesFromIngredients } from '../../components/RecipesFromIngredients/RecipesFromIngredients';
import {
  useCustomization,
  CustomizationStates,
} from '../../providers/CustomizationProvider/CustomizationProvider';
import {
  AllergenConfirmation,
  type AllergenOption,
} from '../AllergenConfirmation/AllergenConfirmation';
import {
  DietConfirmation,
  type DietOption,
} from '../DietConfirmation/DietConfirmation';
import {
  ItemListConfirmation,
  type ItemListConfirmationItem,
} from '../ItemListConfirmation/ItemListConfirmation';
import {
  NeversConfirmation,
  type NeverCategoryOption,
  type NeverProductOption,
} from '../NeversConfirmation/NeversConfirmation';
import {
  NutritionalGoalsConfirmation,
  type NutritionalGoalOption,
} from '../NutritionalGoalsConfirmation/NutritionalGoalsConfirmation';
import {
  RecipeSwapConfirmation,
  type SwapItem,
} from '../RecipeSwapConfirmation/RecipeSwapConfirmation';
import { SmartCartChatFeedback } from '../SmartCartChatFeedback/SmartCartChatFeedback';
import { ToggleSkipConfirmation } from '../ToggleSkipConfirmation/ToggleSkipConfirmation';
import { ChatHistory } from './components/ChatHistory/ChatHistory';
import { ChatInput } from './components/ChatInput/ChatInput';
import { isSpeechRecognitionSupported } from './components/ChatInput/useSpeechRecognition';
import { ChatMessage } from './components/ChatMessage/ChatMessage';
import { ChatWelcome } from './components/ChatWelcome/ChatWelcome';
import { FeedbackButtons } from './components/FeedbackButtons/FeedbackButtons';
import { StreamingMessage } from './components/StreamingMessage/StreamingMessage';
import { TypingIndicator } from './components/TypingIndicator/TypingIndicator';
import { useAIImageFallbackIngredients } from './hooks/useAIImageFallbackIngredients';
import { useSmartCartAgentPanel } from './hooks/useSmartCartAgentPanel';
import { PAIRING_IMAGE_FALLBACK_URL } from './types';
import { consumeSearchAttribution } from './utils/searchAttributionStorage';
import styles from './SmartCartAgentChatContent.module.css';

// Match the TypingIndicator's CSS `transition: opacity 200ms ease`. The
// component fades out via opacity when `isVisible` flips to false; this
// delay keeps it mounted long enough for the fade to complete before
// React removes it from the DOM.
const TYPING_INDICATOR_FADE_OUT_MS = 200;

function useDelayedUnmount(show: boolean, delayMs: number) {
  const [shouldRender, setShouldRender] = useState(show);
  /* eslint-disable react-you-might-not-need-an-effect/you-might-not-need-an-effect */
  useEffect(() => {
    if (show) {
      setShouldRender(true);
      return;
    }
    const timer = setTimeout(() => setShouldRender(false), delayMs);
    return () => clearTimeout(timer);
  }, [show, delayMs]);
  /* eslint-enable react-you-might-not-need-an-effect/you-might-not-need-an-effect */
  return shouldRender;
}

const trackedConversationIds = new Set<string>();

/** Test-only: clears panel-view tracking between cases. */
export function resetScAgentViewTrackingForTests(): void {
  trackedConversationIds.clear();
}
// Rooty chat is only surfaced from the cart drawer or the iOS standalone
// `/smartcart-agent/` page (also opened from the cart), so hardcode
// `page_name='cart'` for all in-chat interaction events.
const CHAT_PAGE_NAME = 'cart';
const CHAT_LOCATION = 'rooty-chat';
const LOW_SODIUM_SWITCH_LABEL = 'I only eat low sodium meals (<560mg)';
const EATING_HABIT_SLUGS = [
  'omnivore',
  'flexitarian',
  'pescatarian',
  'vegetarian',
  'vegan',
] as const;
const EATING_HABIT_FALLBACK_OPTIONS: Array<{ slug: string; label: string }> = [
  { slug: 'omnivore', label: 'Omnivore' },
  { slug: 'flexitarian', label: 'Flexitarian' },
  { slug: 'pescatarian', label: 'Pescatarian' },
  { slug: 'vegetarian', label: 'Vegetarian' },
  { slug: 'vegan', label: 'Vegan' },
];
const ALLERGEN_SLUGS = [
  'dairy-free',
  'gluten-free',
  'soy-free',
  'tree-nut-free',
  'peanut-free',
  'egg-free',
  'shellfish-free',
  'sesame-free',
  'grain-free',
  'garlic-free',
  'onion-free',
  'caffeine-free',
] as const;
const DIETARY_GOAL_FALLBACK_OPTIONS = [
  { id: TagEnum.CarbConscious, label: 'Carb conscious' },
  { id: TagEnum.LessSugar, label: 'Less sugar' },
  { id: TagEnum.HighProtein, label: 'High protein' },
  { id: TagEnum.LowSodium, label: 'Low sodium' },
  { id: TagEnum.AntiInflamitory, label: 'Anti-inflammatory' },
  { id: TagEnum.GutFriendly, label: 'Gut friendly' },
  { id: TagEnum.ImmunityBoosting, label: 'Immunity boosting' },
  { id: TagEnum.HighFiber, label: 'High fiber' },
  { id: TagEnum.LowFat, label: 'Low fat' },
  { id: TagEnum.LowCholesterol, label: 'Low cholesterol' },
  { id: TagEnum.LowFodmap, label: 'Low FODMAP' },
  { id: TagEnum.CalorieConscious, label: 'Calorie conscious' },
];

interface SmartCartAgentChatContentProps {
  isOpen: boolean;
  isHistoryOpen?: boolean;
  onCloseHistory?: () => void;
  isMobilePreview?: boolean;
  onBeforePromptClick?: () => void;
  onPanelStyleChange?: (style: CSSProperties | undefined) => void;
  /** Overrides the surface derived from the route; see `useSmartCartAgentPanel`. */
  surface?: AgentSurface;
  testid?: string;
}

export function SmartCartAgentChatContent({
  isOpen,
  isHistoryOpen = false,
  onCloseHistory,
  isMobilePreview = false,
  onBeforePromptClick,
  onPanelStyleChange,
  surface,
  testid = 'smart-cart-agent-panel',
}: SmartCartAgentChatContentProps) {
  const [rdpPairing, setRdpPairing] = useState<PairingListResultV3 | null>(
    null
  );
  const [pdpProductId, setPdpProductId] = useState<number | null>(null);
  // Snapshot of inputValue at the moment a voice dictation session starts.
  // Used to replace the "dictation tail" of the input on each transcript
  // update so interim results don't append duplicate text.
  const voiceBasePrefixRef = useRef('');

  const customization = useCustomization();
  const orderId =
    customization.state === CustomizationStates.Ready ||
    customization.state === CustomizationStates.Skipped
      ? customization.order.id
      : undefined;

  const isEmbeddedWebView = useIsEmbeddedWebView();

  const showRecipesFromIngredients = useFeatureFlag(
    'web.feature-flag.recipe-from-ingredients.enabled'
  );

  const {
    messages,
    streamingContent,
    isStreaming,
    isWaitingForResponse,
    pendingConfirmation,
    isConfirming,
    showTypingIndicator,
    progressMessage,
    hasInput,
    initData,
    isInitLoading,
    isInitError,
    messagesEndRef,
    messagesContainerRef,
    contentEndRef,
    inputRef,
    panelRef,
    handleSubmit,
    handleInputChange,
    setInputValue,
    handlePromptClick,
    handleKeyDown,
    handleConfirmAction,
    handleCancelConfirmation,
    handleConfirmAllergenUpdate,
    handleCancelAllergenUpdate,
    pendingAllergenUpdate,
    pendingNutritionalGoalsUpdate,
    handleConfirmNutritionalGoalsUpdate,
    handleCancelNutritionalGoalsUpdate,
    pendingDietUpdate,
    handleConfirmDietUpdate,
    handleCancelDietUpdate,
    pendingRecipeSwap,
    handleConfirmRecipeSwap,
    handleCancelRecipeSwap,
    pendingToggleSkipUpdate,
    handleConfirmToggleSkip,
    handleCancelToggleSkip,
    pendingNeversUpdate,
    handleConfirmNeversUpdate,
    handleCancelNeversUpdate,
    scrollToBottom,
    isScrolledToBottom,
    jumpToBottom,
    isLastAnswerAbove,
    jumpToLastAnswer,
    getCartCount,
    getProductCartCount,
    cartMutations,
    conversationId,
    selectConversation,
    startNewChat,
    isStartingNewChat,
    hasNewChatError,
    panelStyle,
    inputValue,
    // Feedback
    feedbackOpenForMessageId,
    messageRatings,
    handleThumbsUp,
    handleThumbsDown,
    handleFeedbackSubmit,
    handleFeedbackClose,
  } = useSmartCartAgentPanel({ isOpen, surface });

  const {
    data: chatHistory,
    isLoading: isHistoryLoading,
    isError: isHistoryError,
  } = useSmartCartAgentChats({ enabled: isOpen && isHistoryOpen });

  const handleSelectChat = useCallback(
    (selectedConversationId: string) => {
      selectConversation(selectedConversationId);
      onCloseHistory?.();
    },
    [selectConversation, onCloseHistory]
  );

  // Hold the overlay open until the fresh conversation exists, so the composer
  // is never briefly pointed at the chat the customer just left.
  const handleNewChat = useCallback(async () => {
    await startNewChat();
    onCloseHistory?.();
  }, [startNewChat, onCloseHistory]);

  // The jump-to-answer arrow overlays the desktop-only results grid; on mobile
  // (which renders the horizontal carousel) it has nothing tall to jump past,
  // so it's a desktop-only affordance.
  const isDesktop = useMedia(BreakpointQueryLarge, false);

  const shouldRenderTypingIndicator = useDelayedUnmount(
    showTypingIndicator,
    TYPING_INDICATOR_FADE_OUT_MS
  );

  const { createFooterButton } = usePdpFooter({
    onAdd: () => setPdpProductId(null),
    onRemove: () => setPdpProductId(null),
    pageName: 'pdp',
    location: null,
    accessedFrom: CHAT_LOCATION,
    conversationId,
  });

  const handleClickPairing = useCallback(
    (pairing: PairingListResultV3) => {
      pairingInteraction({
        pairingId: pairing.id,
        pairingName: pairing.name,
        actionType: 'clicked',
        orderId,
        pageName: CHAT_PAGE_NAME,
        location: CHAT_LOCATION,
        conversationId,
      });
      setRdpPairing(pairing);
    },
    [orderId, conversationId]
  );

  const handleClickProduct = useCallback(
    (product: Product) => {
      productInteraction({
        productId: product.id,
        productName: product.name,
        actionType: 'clicked',
        orderId,
        pageName: CHAT_PAGE_NAME,
        location: CHAT_LOCATION,
        conversationId,
      });
      setPdpProductId(product.id);
    },
    [orderId, conversationId]
  );

  const { ingredientsByPairingId } = useAIImageFallbackIngredients(messages);

  const { data: customerDietaryData } = useCustomerDietaryRestrictions({
    enabled: !!pendingAllergenUpdate,
  });
  const { data: allDietaryData } = useAllDietaryRestrictions({
    enabled: !!pendingAllergenUpdate || !!pendingDietUpdate,
  });
  const { data: customerNutritionData } = useCustomerTagGroup(
    tagGroups.nutrition,
    {
      enabled: !!pendingNutritionalGoalsUpdate,
    }
  );
  const { data: lowSodiumCustomerData } = useCustomerTags(
    [TagEnum.OnlyLowSodium],
    {
      enabled: !!pendingNutritionalGoalsUpdate,
    }
  );
  const nutritionTagGroup = useTagGroup(tagGroups.nutrition, {
    enabled: !!pendingNutritionalGoalsUpdate,
  });

  const [allergenSelections, setAllergenSelections] = useState<
    Record<string, boolean>
  >({});
  const [dietaryGoalSelections, setDietaryGoalSelections] = useState<
    Record<number, boolean>
  >({});
  const [onlyLowSodiumSelection, setOnlyLowSodiumSelection] = useState(false);
  const [selectedDietSlug, setSelectedDietSlug] = useState<string | null>(null);
  const [neverCategorySelections, setNeverCategorySelections] = useState<
    Record<string, boolean>
  >({});
  const [neverProductSelections, setNeverProductSelections] = useState<
    Record<string, boolean>
  >({});
  const [confirmationItemSelections, setConfirmationItemSelections] = useState<{
    signature: string;
    values: Record<string, boolean>;
  }>({ signature: '', values: {} });
  const confirmationItemSelectionsRef = useRef<Record<string, boolean>>({});
  const confirmationItemKeySignatureRef = useRef('');

  const getConfirmationItemKey = useCallback(
    (item: NonNullable<typeof pendingConfirmation>['items'][number]) =>
      `${item.type}-${item.data.id}`,
    []
  );

  const confirmationItemKeySignature = useMemo(() => {
    if (!pendingConfirmation) {
      return '';
    }

    return pendingConfirmation.items.map(getConfirmationItemKey).join('|');
  }, [getConfirmationItemKey, pendingConfirmation]);

  if (
    confirmationItemKeySignatureRef.current !== confirmationItemKeySignature
  ) {
    confirmationItemKeySignatureRef.current = confirmationItemKeySignature;
    confirmationItemSelectionsRef.current = {};
  }

  useEffect(() => {
    if (!pendingAllergenUpdate) return;
    const selections: Record<string, boolean> = {};
    const currentRestrictions = customerDietaryData?.dietary_restrictions ?? [];
    for (const slug of ALLERGEN_SLUGS) {
      const current = currentRestrictions.find((r) => r.slug === slug);
      selections[slug] = current?.selected ?? false;
    }
    for (const a of pendingAllergenUpdate.allergens) {
      selections[a.slug] = a.selected;
    }
    setAllergenSelections(selections);
  }, [pendingAllergenUpdate, customerDietaryData]);

  const allergenOptions: AllergenOption[] = useMemo(() => {
    if (!pendingAllergenUpdate) return [];
    const restrictionsLookup = new Map(
      (allDietaryData?.results ?? []).map((r) => [r.slug, r.name])
    );
    return ALLERGEN_SLUGS.filter((slug) => restrictionsLookup.has(slug)).map(
      (slug) => ({
        id: slug,
        label: restrictionsLookup.get(slug) ?? slug,
        isSelected: allergenSelections[slug] ?? false,
      })
    );
  }, [pendingAllergenUpdate, allDietaryData, allergenSelections]);

  const handleToggleAllergen = useCallback((id: string) => {
    setAllergenSelections((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const handleConfirmAllergenSelection = useCallback(() => {
    handleConfirmAllergenUpdate(
      allergenOptions.map((allergen) => ({
        slug: allergen.id,
        selected: allergen.isSelected,
      }))
    );
  }, [allergenOptions, handleConfirmAllergenUpdate]);

  useEffect(() => {
    if (!pendingNutritionalGoalsUpdate) return;

    const selections: Record<number, boolean> = {};
    const currentSelectedGoals =
      customerNutritionData?.results?.filter((goal) => goal.preference === 1) ??
      [];
    const availableGoals =
      nutritionTagGroup.data?.tags?.map((tag) => ({
        id: tag.id,
        label: tag.name,
      })) ?? DIETARY_GOAL_FALLBACK_OPTIONS;

    for (const goal of availableGoals) {
      selections[goal.id] = currentSelectedGoals.some(
        (currentGoal) => currentGoal.tag === goal.id
      );
    }

    for (const goal of pendingNutritionalGoalsUpdate.goals) {
      selections[goal.tagId] = goal.selected;
    }

    setDietaryGoalSelections(selections);
    setOnlyLowSodiumSelection(
      pendingNutritionalGoalsUpdate.onlyLowSodium ??
        lowSodiumCustomerData?.results?.[0]?.preference === 1
    );
  }, [
    pendingNutritionalGoalsUpdate,
    customerNutritionData,
    lowSodiumCustomerData,
    nutritionTagGroup.data,
  ]);

  const dietaryGoalOptions: NutritionalGoalOption[] = useMemo(() => {
    if (!pendingNutritionalGoalsUpdate) return [];

    const availableGoals =
      nutritionTagGroup.data?.tags?.map((tag) => ({
        id: tag.id,
        label: tag.name,
      })) ?? DIETARY_GOAL_FALLBACK_OPTIONS;

    return availableGoals.map((goal) => ({
      id: String(goal.id),
      label: goal.label,
      isSelected: dietaryGoalSelections[goal.id] ?? false,
    }));
  }, [
    pendingNutritionalGoalsUpdate,
    nutritionTagGroup.data,
    dietaryGoalSelections,
  ]);
  const lowSodiumGoalSelected =
    dietaryGoalSelections[TagEnum.LowSodium] ?? false;

  const handleToggleDietaryGoal = useCallback((id: string) => {
    const tagId = Number(id);
    setDietaryGoalSelections((prev) => {
      const nextValue = !prev[tagId];
      const next = { ...prev, [tagId]: nextValue };
      if (tagId === TagEnum.LowSodium && !nextValue) {
        setOnlyLowSodiumSelection(false);
      }
      return next;
    });
  }, []);

  const handleConfirmDietaryGoalSelection = useCallback(() => {
    handleConfirmNutritionalGoalsUpdate(
      dietaryGoalOptions.map((goal) => ({
        tagId: Number(goal.id),
        selected: goal.isSelected,
      })),
      onlyLowSodiumSelection
    );
  }, [
    dietaryGoalOptions,
    handleConfirmNutritionalGoalsUpdate,
    onlyLowSodiumSelection,
  ]);

  // Seed the selected diet with the agent-suggested slug whenever a new
  // pending diet update arrives.
  useEffect(() => {
    if (!pendingDietUpdate) {
      setSelectedDietSlug(null);
      return;
    }
    setSelectedDietSlug(pendingDietUpdate.slug);
  }, [pendingDietUpdate]);

  const dietOptions: DietOption[] = useMemo(() => {
    if (!pendingDietUpdate) return [];

    // Build label lookup from the dietary restrictions catalog so we show
    // the same display names as the rest of the app. Fall back to the
    // hardcoded list if the fetch hasn't returned yet or is missing a row.
    const catalogLookup = new Map(
      (allDietaryData?.results ?? []).map((r) => [r.slug, r.name])
    );

    return EATING_HABIT_FALLBACK_OPTIONS.map((habit) => ({
      id: habit.slug,
      label: catalogLookup.get(habit.slug) ?? habit.label,
      isSelected: selectedDietSlug === habit.slug,
    }));
  }, [pendingDietUpdate, allDietaryData, selectedDietSlug]);

  const handleSelectDiet = useCallback((id: string) => {
    setSelectedDietSlug(id);
  }, []);

  const handleConfirmDietSelection = useCallback(() => {
    if (!selectedDietSlug) return;
    if (
      !EATING_HABIT_SLUGS.includes(
        selectedDietSlug as (typeof EATING_HABIT_SLUGS)[number]
      )
    ) {
      return;
    }
    handleConfirmDietUpdate(selectedDietSlug);
  }, [selectedDietSlug, handleConfirmDietUpdate]);

  const confirmationItemType = useMemo(() => {
    if (!pendingConfirmation || pendingConfirmation.items.length === 0) {
      return 'items';
    }

    const firstType = pendingConfirmation.items[0].type;
    return pendingConfirmation.items.every((item) => item.type === firstType)
      ? firstType
      : 'items';
  }, [pendingConfirmation]);

  const isRemoveConfirmation = pendingConfirmation?.actionType === 'remove';
  const confirmationVerb = isRemoveConfirmation ? 'Removing' : 'Adding';
  const confirmationNoun =
    confirmationItemType === 'pairing'
      ? 'recipes'
      : confirmationItemType === 'product'
        ? 'products'
        : 'items';
  const confirmationTitle = `${confirmationVerb} ${confirmationNoun}`;

  const confirmationDescription = pendingConfirmation?.description;

  const confirmationListItems: ItemListConfirmationItem[] = useMemo(() => {
    if (!pendingConfirmation) {
      return [];
    }

    const selectionValues =
      confirmationItemSelections.signature === confirmationItemKeySignature
        ? confirmationItemSelections.values
        : confirmationItemSelectionsRef.current;

    return pendingConfirmation.items.map((item) => {
      const key = getConfirmationItemKey(item);
      const rawImageUrl =
        item.type === 'pairing'
          ? item.data.featured_img_url
          : item.data.card_image_url ?? item.data.packaged_image_url;

      return {
        id: key,
        name: item.type === 'pairing' ? item.data.name ?? '' : item.data.name,
        imageUrl: rawImageUrl || PAIRING_IMAGE_FALLBACK_URL,
        points: Number(item.data.points) || 0,
        isSelected: selectionValues[key] ?? true,
      };
    });
  }, [
    confirmationItemKeySignature,
    confirmationItemSelections,
    getConfirmationItemKey,
    pendingConfirmation,
  ]);

  const handleToggleConfirmationItem = useCallback(
    (id: string, isSelected?: boolean) => {
      const next = {
        ...confirmationItemSelectionsRef.current,
        [id]:
          isSelected ?? !(confirmationItemSelectionsRef.current[id] ?? true),
      };
      confirmationItemSelectionsRef.current = next;
      setConfirmationItemSelections({
        signature: confirmationItemKeySignatureRef.current,
        values: next,
      });
    },
    []
  );

  const handleConfirmSelectedItems = useCallback(() => {
    if (!pendingConfirmation) return;

    const selections = confirmationItemSelectionsRef.current;
    handleConfirmAction(
      pendingConfirmation.items.filter(
        (item) => selections[getConfirmationItemKey(item)] ?? true
      )
    );
  }, [getConfirmationItemKey, handleConfirmAction, pendingConfirmation]);

  useEffect(() => {
    if (!pendingNeversUpdate) return;
    setNeverCategorySelections(
      Object.fromEntries(
        pendingNeversUpdate.categories.map((category) => [
          category.name,
          category.selected,
        ])
      )
    );
    setNeverProductSelections(
      Object.fromEntries(
        pendingNeversUpdate.items.map((item) => [String(item.data.id), true])
      )
    );
  }, [pendingNeversUpdate]);

  const neverCategoryOptions: NeverCategoryOption[] = useMemo(() => {
    if (!pendingNeversUpdate) return [];
    return pendingNeversUpdate.categories.map((category) => ({
      name: category.name,
      isSelected: neverCategorySelections[category.name] ?? category.selected,
    }));
  }, [pendingNeversUpdate, neverCategorySelections]);

  const neverProductOptions: NeverProductOption[] = useMemo(() => {
    if (!pendingNeversUpdate) return [];
    return pendingNeversUpdate.items.map((item) => {
      const id = String(item.data.id);
      const rawImageUrl =
        item.type === 'pairing'
          ? item.data.featured_img_url
          : item.data.card_image_url ?? item.data.packaged_image_url;

      return {
        id,
        name: item.data.name ?? '',
        imageUrl: rawImageUrl || PAIRING_IMAGE_FALLBACK_URL,
        isSelected: neverProductSelections[id] ?? true,
      };
    });
  }, [pendingNeversUpdate, neverProductSelections]);

  const handleToggleNeverCategory = useCallback((name: string) => {
    setNeverCategorySelections((prev) => ({ ...prev, [name]: !prev[name] }));
  }, []);

  const handleToggleNeverProduct = useCallback(
    (id: string, isSelected?: boolean) => {
      setNeverProductSelections((prev) => ({
        ...prev,
        [id]: isSelected ?? !(prev[id] ?? true),
      }));
    },
    []
  );

  const handleConfirmNeversSelection = useCallback(() => {
    handleConfirmNeversUpdate(
      neverProductOptions
        .filter((product) => product.isSelected)
        .map((product) => Number(product.id)),
      neverCategoryOptions.map((category) => ({
        name: category.name,
        selected: category.isSelected,
      }))
    );
  }, [neverProductOptions, neverCategoryOptions, handleConfirmNeversUpdate]);

  const recipeSwapItems = useMemo(() => {
    if (!pendingRecipeSwap) return null;

    const toSwapItem = (
      item: typeof pendingRecipeSwap.originalItem
    ): SwapItem => ({
      id: String(item.data.id),
      name: item.data.name ?? '',
      imageUrl: item.data.featured_img_url || PAIRING_IMAGE_FALLBACK_URL,
      points: Number(item.data.points) || 0,
    });

    return {
      originalItem: toSwapItem(pendingRecipeSwap.originalItem),
      replacementItem: toSwapItem(pendingRecipeSwap.replacementItem),
    };
  }, [pendingRecipeSwap]);

  const enrichedRdpPairing = useMemo(() => {
    if (!rdpPairing) return null as never;
    const basePairing =
      convertPairingListResultV3ToPairingV1WithIngredients(rdpPairing);
    const enrichedIngredients = ingredientsByPairingId.get(rdpPairing.id);
    if (!enrichedIngredients?.length) return basePairing;

    return {
      ...basePairing,
      ingredients: basePairing.ingredients.map((ingredient) => {
        const enriched = enrichedIngredients.find(
          (e) => e.id === ingredient.id
        );
        return enriched ? { ...ingredient, ...enriched } : ingredient;
      }),
    };
  }, [rdpPairing, ingredientsByPairingId]);

  useEffect(() => {
    const conversationId = initData?.conversationId;
    if (
      isOpen &&
      conversationId &&
      !trackedConversationIds.has(conversationId)
    ) {
      trackedConversationIds.add(conversationId);

      const fullStoryLink = getFullStorySessionUrl();
      // Present only when this conversation was opened from search (Exp 224):
      // a prompt click or the "Chat with Rooty" button stores it, and reading
      // it here consumes it so a later panel open from the cart or nav is not
      // wrongly attributed to a search.
      const searchId = consumeSearchAttribution() ?? undefined;
      componentInteraction({
        actionType: 'viewed',
        componentType: 'panel',
        componentName: 'sc_agent',
        orderId,
        conversationId,
        searchId,
        fullStoryLink,
      });
      if (isSpeechRecognitionSupported()) {
        componentInteraction({
          actionType: 'viewed',
          componentType: 'page_section',
          componentName: 'voice_input',
          orderId,
          conversationId,
        });
      }
    }
  }, [isOpen, initData?.conversationId, orderId]);

  // Notify parent (SmartCartAgentPanel) of keyboard-driven style changes so it
  // can apply them to the fixed-position panel container rather than the inner
  // content wrapper — the fixed container is the element that must shrink when
  // the iOS keyboard opens.
  /* eslint-disable react-you-might-not-need-an-effect/you-might-not-need-an-effect */
  useEffect(() => {
    onPanelStyleChange?.(panelStyle);
  }, [panelStyle, onPanelStyleChange]);
  /* eslint-enable react-you-might-not-need-an-effect/you-might-not-need-an-effect */

  const resolvedPromptClick = useCallback(
    (label: string, type: SuggestedPromptVariant) => {
      onBeforePromptClick?.();
      handlePromptClick(label, type);
    },
    [onBeforePromptClick, handlePromptClick]
  );

  return (
    <>
      <div
        ref={panelRef}
        data-testid={`${testid}-content`}
        className={styles.wrapper}
      >
        {isDesktop &&
          isLastAnswerAbove &&
          !isStreaming &&
          !isWaitingForResponse && (
            <button
              type="button"
              className={styles['jump-to-answer']}
              onClick={jumpToLastAnswer}
              aria-label="Scroll to latest answer"
            >
              <CaretUp />
            </button>
          )}
        <div
          ref={messagesContainerRef}
          className={styles['messages-container']}
          data-chat-messages
        >
          {messages.length === 0 ? (
            <ChatWelcome
              isLoading={isInitLoading}
              isError={isInitError}
              isPreviewMode={isMobilePreview}
              welcomeText={initData?.welcomeText}
              introText={initData?.introText}
              suggestedPrompts={initData?.suggestedPrompts}
              onPromptClick={resolvedPromptClick}
              isDisabled={isStreaming || isWaitingForResponse}
              testid={testid}
            />
          ) : (
            messages.map((message) => (
              <div key={message.id} data-message-type={message.type}>
                <ChatMessage
                  message={message}
                  testid={testid}
                  onAddPairing={(pairingV1) =>
                    cartMutations.addPairing({
                      pairing: pairingV1,
                      pageName: CHAT_PAGE_NAME,
                      location: CHAT_LOCATION,
                      conversationId,
                    })
                  }
                  onRemovePairing={(pairingV1) =>
                    cartMutations.removePairing({
                      pairing: pairingV1,
                      pageName: CHAT_PAGE_NAME,
                      location: CHAT_LOCATION,
                      conversationId,
                    })
                  }
                  onAddProduct={(productV1) =>
                    cartMutations.addProduct({
                      product: productV1,
                      pageName: CHAT_PAGE_NAME,
                      location: CHAT_LOCATION,
                      conversationId,
                    })
                  }
                  onRemoveProduct={(productV1) =>
                    cartMutations.removeProduct({
                      product: productV1,
                      pageName: CHAT_PAGE_NAME,
                      location: CHAT_LOCATION,
                      conversationId,
                    })
                  }
                  onClickPairing={handleClickPairing}
                  onClickProduct={handleClickProduct}
                  getCartCount={getCartCount}
                  getProductCartCount={getProductCartCount}
                  ingredientsByPairingId={ingredientsByPairingId}
                  // Feedback props
                  rating={messageRatings[message.id]}
                  onThumbsUp={() =>
                    handleThumbsUp(message.id, message.messageId)
                  }
                  onThumbsDown={() =>
                    handleThumbsDown(message.id, message.messageId)
                  }
                />
                {feedbackOpenForMessageId === message.id && (
                  <SmartCartChatFeedback
                    testid={`${testid}-feedback-${message.id}`}
                    rating={messageRatings[message.id]}
                    onSubmit={(feedback) =>
                      handleFeedbackSubmit(
                        message.id,
                        message.messageId,
                        feedback
                      )
                    }
                    onClose={handleFeedbackClose}
                  />
                )}
              </div>
            ))
          )}

          <StreamingMessage content={streamingContent} testid={testid} />

          {shouldRenderTypingIndicator && (
            <TypingIndicator
              message={progressMessage}
              isVisible={showTypingIndicator}
              testid={testid}
            />
          )}

          {pendingConfirmation && (
            <ItemListConfirmation
              testid={`${testid}-confirmation`}
              title={confirmationTitle}
              description={confirmationDescription}
              items={confirmationListItems}
              onToggleItem={handleToggleConfirmationItem}
              onConfirm={handleConfirmSelectedItems}
              onCancel={handleCancelConfirmation}
              confirmText={isRemoveConfirmation ? 'Remove' : 'Confirm'}
              isLoading={isConfirming}
            />
          )}
          {pendingConfirmation?.messageId && (
            <>
              <FeedbackButtons
                rating={messageRatings[pendingConfirmation.messageId]}
                onThumbsUp={() =>
                  handleThumbsUp(
                    pendingConfirmation.messageId!,
                    pendingConfirmation.messageId
                  )
                }
                onThumbsDown={() =>
                  handleThumbsDown(
                    pendingConfirmation.messageId!,
                    pendingConfirmation.messageId
                  )
                }
                testid={`${testid}-confirmation`}
              />
              {feedbackOpenForMessageId === pendingConfirmation.messageId && (
                <SmartCartChatFeedback
                  testid={`${testid}-confirmation-feedback`}
                  rating={messageRatings[pendingConfirmation.messageId]}
                  onSubmit={(feedback) =>
                    handleFeedbackSubmit(
                      pendingConfirmation.messageId!,
                      pendingConfirmation.messageId,
                      feedback
                    )
                  }
                  onClose={handleFeedbackClose}
                />
              )}
            </>
          )}
          {pendingAllergenUpdate && allergenOptions.length > 0 && (
            <AllergenConfirmation
              testid={`${testid}-allergen-confirmation`}
              description={pendingAllergenUpdate.description}
              allergens={allergenOptions}
              onToggleAllergen={handleToggleAllergen}
              onConfirm={handleConfirmAllergenSelection}
              onCancel={handleCancelAllergenUpdate}
              isLoading={isConfirming}
            />
          )}
          {pendingNutritionalGoalsUpdate && dietaryGoalOptions.length > 0 && (
            <NutritionalGoalsConfirmation
              testid={`${testid}-nutritional-goals-confirmation`}
              description={pendingNutritionalGoalsUpdate.description}
              goals={dietaryGoalOptions}
              onToggleGoal={handleToggleDietaryGoal}
              switchLabel={
                lowSodiumGoalSelected ? LOW_SODIUM_SWITCH_LABEL : undefined
              }
              switchEnabled={onlyLowSodiumSelection}
              onSwitchChange={
                lowSodiumGoalSelected ? setOnlyLowSodiumSelection : undefined
              }
              onConfirm={handleConfirmDietaryGoalSelection}
              onCancel={handleCancelNutritionalGoalsUpdate}
              isLoading={isConfirming}
            />
          )}
          {pendingDietUpdate && dietOptions.length > 0 && (
            <DietConfirmation
              testid={`${testid}-diet-confirmation`}
              description={pendingDietUpdate.description}
              diets={dietOptions}
              onSelectDiet={handleSelectDiet}
              onConfirm={handleConfirmDietSelection}
              onCancel={handleCancelDietUpdate}
              isLoading={isConfirming}
            />
          )}
          {pendingRecipeSwap && recipeSwapItems && (
            <RecipeSwapConfirmation
              testid={`${testid}-recipe-swap-confirmation`}
              description={pendingRecipeSwap.description}
              originalItem={recipeSwapItems.originalItem}
              replacementItem={recipeSwapItems.replacementItem}
              onConfirm={handleConfirmRecipeSwap}
              onCancel={handleCancelRecipeSwap}
              isLoading={isConfirming}
            />
          )}
          {pendingNeversUpdate && (
            <NeversConfirmation
              testid={`${testid}-nevers-confirmation`}
              description={pendingNeversUpdate.description}
              categories={neverCategoryOptions}
              products={neverProductOptions}
              onToggleCategory={handleToggleNeverCategory}
              onToggleProduct={handleToggleNeverProduct}
              onConfirm={handleConfirmNeversSelection}
              onCancel={handleCancelNeversUpdate}
              isLoading={isConfirming}
            />
          )}
          {pendingToggleSkipUpdate && (
            <ToggleSkipConfirmation
              testid={`${testid}-toggle-skip-confirmation`}
              description={pendingToggleSkipUpdate.description}
              onConfirm={handleConfirmToggleSkip}
              onCancel={handleCancelToggleSkip}
              isLoading={isConfirming}
            />
          )}
          <div ref={contentEndRef} />
          <div ref={messagesEndRef} />
        </div>

        <div className={styles['input-area']} data-chat-input>
          {!isScrolledToBottom && !isStreaming && !isWaitingForResponse && (
            <button
              type="button"
              className={styles['scroll-to-bottom']}
              onClick={jumpToBottom}
              aria-label="Scroll to bottom"
            >
              <CaretDown />
            </button>
          )}
          <ChatInput
            inputRef={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onSubmit={handleSubmit}
            onFocus={() => {
              setTimeout(() => {
                scrollToBottom();
              }, 100);
            }}
            isDisabled={false}
            isSendDisabled={
              isStreaming || isWaitingForResponse || isInitLoading
            }
            hasInput={hasInput}
            isWebView={isEmbeddedWebView}
            isPanelOpen={isOpen}
            onVoiceButtonClicked={() => {
              buttonClickedEvent({
                button_name: 'voice_input',
                order_id: orderId ?? null,
                page_position: '',
                conversation_id: conversationId,
                full_story_link: getFullStorySessionUrl(),
              });
            }}
            onVoiceSessionStart={() => {
              const base = inputValue;
              voiceBasePrefixRef.current =
                base.length > 0 && !base.endsWith(' ') ? `${base} ` : base;
            }}
            onVoiceTranscriptUpdate={(text) => {
              setInputValue(`${voiceBasePrefixRef.current}${text}`);
            }}
            testid={testid}
          />
        </div>
      </div>
      {rdpPairing && (
        <RDPModal
          initialPairing={enrichedRdpPairing}
          orderId={orderId}
          onDismiss={() => setRdpPairing(null)}
          testid={`${testid}-rdp-modal`}
          interactionOverrides={{
            location: null,
            pageName: 'rdp',
            accessedFrom: CHAT_LOCATION,
            conversationId,
          }}
        />
      )}
      {pdpProductId && (
        <PDPModal
          stacked={false}
          id={pdpProductId}
          onDismiss={() => setPdpProductId(null)}
          createFooterButton={createFooterButton}
          showOSNSection={true}
          enableFoodProfileNeverWarnings={true}
          recipesSection={
            showRecipesFromIngredients ? (
              <RecipesFromIngredients
                productId={pdpProductId}
                onDismiss={() => setPdpProductId(null)}
              />
            ) : undefined
          }
          interactionOverrides={{
            location: null,
            pageName: 'pdp',
            accessedFrom: CHAT_LOCATION,
            conversationId,
          }}
        />
      )}
      {isHistoryOpen && (
        <div className={styles['history-overlay']}>
          <ChatHistory
            chats={chatHistory?.chats ?? []}
            isLoading={isHistoryLoading || isStartingNewChat}
            isError={isHistoryError}
            onSelect={handleSelectChat}
            onNewChat={handleNewChat}
            hasNewChatError={hasNewChatError}
            onDismiss={() => onCloseHistory?.()}
            testid={`${testid}-history`}
          />
        </div>
      )}
    </>
  );
}
